import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import ProjectResourcesDialog from '../components/ProjectResourcesDialog';
import { triggerForceDownload } from '../components/fileVisuals';
import {
  canOpenProjectMaterials,
  projectMaterialsLabel,
} from '../utils/materialsLabels';
import {
  projectEssentialApi,
  type GlsLandSaleItem,
  glsLinkedProjectId,
} from '../services/api';
import { getCachedAnalyticsCea } from '../services/analytics';

// ─── LandInfo formatters ─────────────────────────────────────────────

function withLandReportUid(downloadLink: string, ceaNumber: string | null | undefined): string {
  const uid = (ceaNumber || '').trim();
  if (!uid) return downloadLink;
  try {
    const url = new URL(downloadLink);
    url.searchParams.set('uid', uid);
    return url.toString();
  } catch {
    const sep = downloadLink.includes('?') ? '&' : '?';
    return `${downloadLink}${sep}uid=${encodeURIComponent(uid)}`;
  }
}

function landReportFilename(downloadLink: string, siteName: string | null | undefined): string {
  try {
    const path = new URL(downloadLink).pathname;
    const base = path.split('/').pop() || '';
    if (base && /\.[a-z0-9]+$/i.test(base)) return decodeURIComponent(base);
  } catch {
    // ignore
  }
  const slug = (siteName || 'land-report')
    .trim()
    .replace(/[^\w\-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
  return `${slug || 'land-report'}.pdf`;
}

function formatSqft(sqft: number | null): string | null {
  if (sqft == null) return null;
  return `${sqft.toLocaleString()} sqft`;
}

function formatLandPrice(price: number | null): string | null {
  if (price == null) return null;
  if (price >= 1_000_000_000) return `$${(price / 1_000_000_000).toFixed(2)}B`;
  if (price >= 1_000_000) return `$${(price / 1_000_000).toFixed(2)}M`;
  return `$${price.toLocaleString()}`;
}

function formatAwardDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '110px 1fr', sm: '150px 1fr' },
        gap: { xs: 1, sm: 2 },
        py: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 600,
          fontSize: '0.7rem',
          alignSelf: 'center',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: 'var(--color-text-strong)', fontWeight: 500, wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function LandInfoPanel({ item }: { item: GlsLandSaleItem }) {
  const locationLine =
    [item.region, item.district, item.planningArea]
      .filter((s): s is string => Boolean(s && s.trim()))
      .join(' | ') || item.location || null;
  const housingUnits = item.housingUnits != null ? item.housingUnits.toLocaleString() : null;
  const awarded = formatAwardDate(item.awardDate);
  const siteArea = formatSqft(item.siteAreaSqft);
  const landPrice = formatLandPrice(item.successfulTenderPrice);
  const psfPpr =
    item.grossPlotRatioSqft != null
      ? `$${item.grossPlotRatioSqft.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })} psf`
      : null;

  return (
    <Stack divider={<Divider flexItem sx={{ borderColor: 'var(--color-border)' }} />}>
      <DetailRow label="Site Name" value={item.name || '—'} />
      <DetailRow label="Developer" value={item.successfulTenderer || '—'} />
      <DetailRow label="Estimated Units" value={housingUnits || '—'} />
      <DetailRow label="Awarded Date" value={awarded || '—'} />
      <DetailRow label="Location" value={locationLine || '—'} />
      <DetailRow label="Nearby MRT" value={item.nearbyMrtStation || '—'} />
      <DetailRow label="Tenure" value={item.tenure || item.leasePeriod || '—'} />
      <DetailRow label="Site Area" value={siteArea || '—'} />
      <DetailRow label="Land Price" value={landPrice || '—'} />
      <DetailRow label="PSF PPR" value={psfPpr || '—'} />
      <DetailRow label="URA Estimated TOP" value={item.uraExpectedTop || '—'} />
      {item.siteStatus && <DetailRow label="Site Status" value={item.siteStatus} />}
      {item.dateOfLaunchEst && <DetailRow label="Est. Launch" value={item.dateOfLaunchEst} />}
    </Stack>
  );
}

export default function GlsLandSaleDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<GlsLandSaleItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    projectEssentialApi
      .getGlsLandSaleDetail(id)
      .then((res) => {
        if (mounted) setItem(res);
      })
      .catch((err) => {
        if (mounted) setError(err?.message || 'Failed to load GLS land sale');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [id]);

  const locationLine = item
    ? [item.region, item.district, item.planningArea]
        .filter(Boolean)
        .join(' | ') ||
      item.location ||
      null
    : null;

  return (
    <Box>
      <Button
        onClick={() => navigate(-1)}
        startIcon={<ArrowBackIcon />}
        variant="outlined"
        size="small"
        sx={{
          mb: { xs: 1.5, sm: 2 },
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 1.5,
          borderColor: 'var(--color-border-strong)',
          color: 'var(--color-text-strong)',
          bgcolor: 'var(--color-surface)',
          px: 1.5,
          py: 0.5,
          '&:hover': {
            borderColor: 'var(--color-accent)',
            bgcolor: 'var(--color-accent-soft)',
            color: 'var(--color-accent-strong)',
          },
        }}
      >
        Back
      </Button>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : !item ? (
        <Typography sx={{ color: 'var(--color-text-muted)' }}>GLS land sale not found.</Typography>
      ) : (
        <Card
          sx={{
            bgcolor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--color-shadow-card)',
            borderRadius: { xs: 2, sm: 3 },
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: { xs: '16 / 10', sm: '16 / 9' },
                maxHeight: { md: 520 },
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
                mb: { xs: 2, sm: 2.5 },
              }}
            >
              {item.coverImage ? (
                <Box
                  component="img"
                  src={item.coverImage}
                  alt={item.name || String(item.id)}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  No image available
                </Box>
              )}
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: 'var(--color-text-strong)',
                lineHeight: 1.2,
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              }}
            >
              {item.name || String(item.id)}
            </Typography>

            {locationLine && (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5 }}>
                <LocationOnIcon sx={{ fontSize: 18, color: 'var(--color-text-muted)' }} />
                <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                  {locationLine}
                </Typography>
              </Stack>
            )}

            {!item?.newProjectId?.trim() && (
              <Button
                onClick={() => {
                  if (!canOpenProjectMaterials(item?.hasMaterials)) return;
                  setResourcesOpen(true);
                }}
                variant="contained"
                startIcon={<FolderOpenOutlinedIcon />}
                fullWidth
                disableElevation
                disabled={!canOpenProjectMaterials(item?.hasMaterials)}
                sx={{
                  mt: { xs: 2, sm: 2.5 },
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  py: { xs: 1, sm: 1.1 },
                  borderRadius: 1.5,
                  bgcolor: 'var(--color-accent)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                }}
              >
                {projectMaterialsLabel(item?.hasMaterials)}
              </Button>
            )}

            {item.downloadLink && (
              <Button
                onClick={async () => {
                  const link = item.downloadLink;
                  if (!link || downloadLoading) return;
                  const href = withLandReportUid(link, getCachedAnalyticsCea());
                  const filename = landReportFilename(href, item.name);
                  setDownloadLoading(true);
                  try {
                    await triggerForceDownload(href, filename);
                  } finally {
                    setDownloadLoading(false);
                  }
                }}
                variant="outlined"
                disabled={downloadLoading}
                startIcon={
                  downloadLoading ? (
                    <CircularProgress size={18} thickness={5} color="inherit" />
                  ) : (
                    <FileDownloadIcon />
                  )
                }
                fullWidth
                sx={{
                  mt: { xs: 2, sm: 2.5 },
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  py: { xs: 1, sm: 1.1 },
                  borderRadius: 1.5,
                  color: 'var(--color-accent-strong)',
                  borderColor: 'var(--color-accent)',
                  '&:hover': {
                    borderColor: 'var(--color-accent-strong)',
                    bgcolor: 'var(--color-accent-soft)',
                  },
                }}
              >
                {downloadLoading ? 'Preparing download…' : 'Download Land Report'}
              </Button>
            )}

            <Box
              sx={{
                mt: { xs: 2, sm: 2.5 },
                borderBottom: '1px solid var(--color-border)',
                pb: 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.08em',
                  color: 'var(--color-accent-strong)',
                  display: 'block',
                  pb: 1,
                }}
              >
                Land Info
              </Typography>
            </Box>

            <Box sx={{ pt: { xs: 1, sm: 1.5 } }}>
              <LandInfoPanel item={item} />
            </Box>
          </Box>
        </Card>
      )}

      {item && (
        <ProjectResourcesDialog
          open={resourcesOpen}
          onClose={() => setResourcesOpen(false)}
          projectId={glsLinkedProjectId(item) || String(item.id)}
          projectName={item.name}
          resourceSource={glsLinkedProjectId(item) ? 'project' : 'gls'}
          isUpcomingGls
        />
      )}
    </Box>
  );
}
